import React, { useEffect, useState } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Pencil, X, Plus, Check, AlertTriangle } from "lucide-react";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const PROGRAMS = ["Primary","K-12","Teens","IB","Cambridge","A-Level"];
const LEVELS = ["Beginner","Intermediate","Advanced"];

const EMPTY_SCHED = { name:"", name_zh:"", program:"K-12", teacher:"", day_of_week:"Mon", days_of_week:["Mon"], time_slot:"", level:"Intermediate", seats_total:8, seats_taken:0, status:"active", start_date:"", end_date:"" };

function DaysOfWeekPicker({ value, onChange, prefix }) {
  const days = value && value.length ? value : ["Mon"];
  function toggle(d) {
    const next = days.includes(d) ? days.filter(x => x !== d) : [...days, d];
    // Keep canonical order Mon..Sun
    next.sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
    onChange(next.length ? next : ["Mon"]);
  }
  return (
    <div data-testid={`sched-${prefix}-days`} className="flex flex-wrap gap-1.5 mt-2">
      {DAYS.map(d => {
        const on = days.includes(d);
        return (
          <button
            key={d}
            type="button"
            onClick={()=>toggle(d)}
            data-testid={`sched-${prefix}-day-${d}`}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${on ? 'bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-[hsl(var(--foreground))]' : 'bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--foreground))]/40'}`}
          >{d}</button>
        );
      })}
    </div>
  );
}

function ScheduleFields({ draft, setDraft, prefix }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  return (
    <>
      <div className="md:col-span-4">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Class name (EN)</Label>
        <Input required value={draft.name} onChange={(e)=>set("name", e.target.value)} data-testid={`sched-${prefix}-name`} className="mt-2" />
      </div>
      <div className="md:col-span-4">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Class name (中文)</Label>
        <Input value={draft.name_zh} onChange={(e)=>set("name_zh", e.target.value)} data-testid={`sched-${prefix}-name-zh`} className="mt-2" />
      </div>
      <div className="md:col-span-4">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Teacher</Label>
        <Input required value={draft.teacher} onChange={(e)=>set("teacher", e.target.value)} data-testid={`sched-${prefix}-teacher`} className="mt-2" />
      </div>
      <div className="md:col-span-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Program</Label>
        <Select value={draft.program} onValueChange={(v)=>set("program", v)}>
          <SelectTrigger data-testid={`sched-${prefix}-program`} className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{PROGRAMS.map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-12">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Recurs on (pick one or more)</Label>
        <DaysOfWeekPicker value={draft.days_of_week} onChange={(v)=>setDraft({ ...draft, days_of_week: v, day_of_week: v[0] })} prefix={prefix} />
      </div>
      <div className="md:col-span-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Time slot</Label>
        <Input required placeholder="16:00 - 17:30" value={draft.time_slot} onChange={(e)=>set("time_slot", e.target.value)} data-testid={`sched-${prefix}-time`} className="mt-2" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Level</Label>
        <Select value={draft.level} onValueChange={(v)=>set("level", v)}>
          <SelectTrigger data-testid={`sched-${prefix}-level`} className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>{LEVELS.map(l=><SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Total</Label>
        <Input required type="number" min={1} value={draft.seats_total} onChange={(e)=>set("seats_total", e.target.value)} data-testid={`sched-${prefix}-seats-total`} className="mt-2" />
      </div>
      <div className="md:col-span-1">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Taken</Label>
        <Input required type="number" min={0} value={draft.seats_taken} onChange={(e)=>set("seats_taken", e.target.value)} data-testid={`sched-${prefix}-seats-taken`} className="mt-2" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Status</Label>
        <Select value={draft.status || "active"} onValueChange={(v)=>set("status", v)}>
          <SelectTrigger data-testid={`sched-${prefix}-status`} className="mt-2"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active" data-testid={`sched-${prefix}-status-active`}>Active (running)</SelectItem>
            <SelectItem value="planned" data-testid={`sched-${prefix}-status-planned`}>Planned (coming soon)</SelectItem>
            <SelectItem value="suspended" data-testid={`sched-${prefix}-status-suspended`}>Suspended (red light)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Start date{draft.status === "planned" ? " *" : ""}</Label>
        <Input type="date" value={draft.start_date || ""} onChange={(e)=>set("start_date", e.target.value)} data-testid={`sched-${prefix}-start-date`} className="mt-2" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">End date <span className="italic text-[hsl(var(--muted-foreground))]/70 normal-case">(optional)</span></Label>
        <Input type="date" value={draft.end_date || ""} min={draft.start_date || undefined} onChange={(e)=>set("end_date", e.target.value)} data-testid={`sched-${prefix}-end-date`} className="mt-2" />
      </div>
    </>
  );
}

export default function SchedulesPanel() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_SCHED);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState(EMPTY_SCHED);
  const [busy, setBusy] = useState(false);
  const [conflictModal, setConflictModal] = useState(null); // { conflicts, suggestion, retryFn }

  async function load() {
    try { const { data } = await api.get("/schedules"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  async function postSchedule(payload, force) {
    return api.post(`/schedules${force ? "?force=true" : ""}`, payload);
  }
  async function patchSchedule(id, payload, force) {
    return api.patch(`/schedules/${id}${force ? "?force=true" : ""}`, payload);
  }

  async function create(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (newDraft.status === "planned" && !newDraft.start_date) {
        toast.error("A planned class needs a Start date so visitors know when it opens.");
        setBusy(false); return;
      }
      const payload = { ...newDraft, seats_total: Number(newDraft.seats_total), seats_taken: Number(newDraft.seats_taken) };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      try {
        await postSchedule(payload, false);
        toast.success("Class added"); setNewDraft(EMPTY_SCHED); setCreating(false); load();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail?.conflicts) {
          setConflictModal({
            conflicts: detail.conflicts,
            suggestion: detail.suggestion,
            label: "Add this class",
            apply: async () => {
              await postSchedule(payload, true);
              toast.success("Class added (override)"); setNewDraft(EMPTY_SCHED); setCreating(false); setConflictModal(null); load();
            },
            applySuggestion: detail.suggestion ? async () => {
              const next = { ...payload, time_slot: detail.suggestion };
              try {
                await postSchedule(next, false);
                toast.success(`Class added at ${detail.suggestion}`); setNewDraft(EMPTY_SCHED); setCreating(false); setConflictModal(null); load();
              } catch (err2) { toast.error(getErrorDetail(err2)); }
            } : null,
          });
        } else { toast.error(getErrorDetail(err)); }
      }
    } finally { setBusy(false); }
  }

  function startEdit(s) {
    setEditingId(s.id);
    const days = (s.days_of_week && s.days_of_week.length) ? s.days_of_week : (s.day_of_week ? [s.day_of_week] : ["Mon"]);
    setDraft({
      name: s.name||"", name_zh: s.name_zh||"", program: s.program||"K-12",
      teacher: s.teacher||"",
      day_of_week: days[0],
      days_of_week: days,
      time_slot: s.time_slot||"", level: s.level||"Intermediate",
      seats_total: s.seats_total||0, seats_taken: s.seats_taken||0,
      status: s.status || "active",
      start_date: s.start_date || "",
      end_date: s.end_date || "",
    });
  }
  function cancelEdit() { setEditingId(null); setDraft(EMPTY_SCHED); }
  async function saveEdit(id) {
    setBusy(true);
    try {
      if (draft.status === "planned" && !draft.start_date) {
        toast.error("A planned class needs a Start date so visitors know when it opens.");
        setBusy(false); return;
      }
      const payload = { ...draft, seats_total: Number(draft.seats_total), seats_taken: Number(draft.seats_taken) };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      try {
        await patchSchedule(id, payload, false);
        toast.success("Class updated"); cancelEdit(); load();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 409 && detail?.conflicts) {
          setConflictModal({
            conflicts: detail.conflicts,
            suggestion: detail.suggestion,
            label: "Save this change",
            apply: async () => {
              await patchSchedule(id, payload, true);
              toast.success("Saved (override)"); cancelEdit(); setConflictModal(null); load();
            },
            applySuggestion: detail.suggestion ? async () => {
              const next = { ...payload, time_slot: detail.suggestion };
              try {
                await patchSchedule(id, next, false);
                toast.success(`Saved at ${detail.suggestion}`); cancelEdit(); setConflictModal(null); load();
              } catch (err2) { toast.error(getErrorDetail(err2)); }
            } : null,
          });
        } else { toast.error(getErrorDetail(err)); }
      }
    } finally { setBusy(false); }
  }
  async function del(id) {
    if (!window.confirm("Delete this class?")) return;
    try { await api.delete(`/schedules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  return (
    <div className="space-y-6" data-testid="admin-schedules-panel">
      {conflictModal && (
        <div data-testid="sched-conflict-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[hsl(var(--border))] max-w-lg w-full p-6">
            <div className="flex items-center gap-2 text-[hsl(var(--destructive))]">
              <AlertTriangle className="w-5 h-5" />
              <h3 className="font-display text-lg tracking-tight">Class arrangement conflict</h3>
            </div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">We blocked the save because of the following:</p>
            <ul className="mt-3 space-y-2">
              {conflictModal.conflicts.map((c, i) => (
                <li key={i} data-testid={`sched-conflict-${i}`} className="text-sm rounded-lg border border-[hsl(var(--destructive))]/20 bg-[hsl(var(--destructive))]/5 p-3">
                  <span className="font-medium text-[hsl(var(--destructive))]">{c.type.replace(/_/g, " ")}</span>
                  <div className="text-[hsl(var(--foreground))] mt-0.5">({c.reason})</div>
                </li>
              ))}
            </ul>
            {conflictModal.suggestion && (
              <div className="mt-4 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3 text-sm">
                💡 Suggested next free slot for this teacher: <strong data-testid="sched-suggestion">{conflictModal.suggestion}</strong>
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <Button variant="outline" onClick={()=>setConflictModal(null)} data-testid="sched-conflict-cancel" className="rounded-full">Cancel</Button>
              {conflictModal.applySuggestion && (
                <Button onClick={conflictModal.applySuggestion} data-testid="sched-conflict-apply-suggestion" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">Use {conflictModal.suggestion}</Button>
              )}
              <Button onClick={conflictModal.apply} data-testid="sched-conflict-override" variant="destructive" className="rounded-full">{conflictModal.label} anyway</Button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">Class Schedules</h3>
        <Button size="sm" onClick={()=>setCreating((v)=>!v)} data-testid="admin-sched-add-toggle" className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white">
          {creating ? <><X className="w-4 h-4 mr-1.5" />Cancel</> : <><Plus className="w-4 h-4 mr-1.5" />New class</>}
        </Button>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-end" data-testid="admin-sched-create-form">
          <ScheduleFields draft={newDraft} setDraft={setNewDraft} prefix="new" />
          <div className="md:col-span-12">
            <Button type="submit" disabled={busy} data-testid="admin-sched-create-submit" className="rounded-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))]">
              {busy ? "…" : "Add class"}
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto bg-white border border-[hsl(var(--border))] rounded-2xl">
        <table className="w-full text-sm" data-testid="admin-sched-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
              <th className="p-4 font-normal">Class</th><th className="p-4 font-normal">Program</th><th className="p-4 font-normal">Teacher</th><th className="p-4 font-normal">Day</th><th className="p-4 font-normal">Time</th><th className="p-4 font-normal">Level</th><th className="p-4 font-normal">Seats</th><th className="p-4 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => editingId === s.id ? (
              <tr key={s.id} data-testid={`admin-sched-row-${s.id}`} className="border-b border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/40">
                <td colSpan={8} className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <ScheduleFields draft={draft} setDraft={setDraft} prefix={`edit-${s.id}`} />
                    <div className="md:col-span-12 flex gap-2">
                      <Button size="sm" type="button" disabled={busy} onClick={()=>saveEdit(s.id)} data-testid={`admin-sched-save-${s.id}`} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"><Check className="w-4 h-4 mr-1.5" />Save</Button>
                      <Button size="sm" type="button" variant="outline" onClick={cancelEdit} data-testid={`admin-sched-cancel-${s.id}`} className="rounded-full"><X className="w-4 h-4 mr-1.5" />Cancel</Button>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={s.id} data-testid={`admin-sched-row-${s.id}`} className="border-b border-[hsl(var(--border))]/60 last:border-0">
                <td className="p-4">{s.name}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{s.program}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{s.teacher}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{(s.days_of_week && s.days_of_week.length) ? s.days_of_week.join(" · ") : s.day_of_week}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{s.time_slot}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{s.level}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">{s.seats_taken}/{s.seats_total}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>startEdit(s)} data-testid={`admin-sched-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(s.id)} data-testid={`admin-sched-delete-${s.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-[hsl(var(--muted-foreground))]">—</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
